/**
 * Canonical workflow contract for the FixControl governance state machine.
 *
 * Codify-what-works: this module describes the lifecycle the orchestration
 * engine already runs, named consistently, with the transition table as
 * the single source of truth. It is NOT a greenfield model — it is the
 * static description of the working dynamic so that:
 *
 *   1. Every channel (UI, Slack, e-mail, GitHub/GitLab webhooks) refers to
 *      the same state names + transition language.
 *   2. The fase-1 success criterion can be machine-checked: every mission
 *      maps 1-on-1 onto a canonical state; the transition map refuses any
 *      non-allowed transition.
 *   3. A future engine refactor can adopt this map as the actual guard
 *      surface without re-deriving the rules from a dozen route files.
 *
 * The canonical states correspond to milestones a human operator should be
 * able to point at in the UI / Slack card / e-mail audit. The current
 * `RunStatus` enum is a narrower projection (queued/planning/running/…);
 * `mapRunStatusToWorkflowState` provides the lossy projection of the
 * current persisted status onto the canonical contract — when the engine
 * is rewritten to persist the canonical state directly, this projection
 * goes away.
 *
 * What this module deliberately is NOT:
 *  - It is NOT an executor. It is not invoked by the engine today; it is
 *    the contract the engine will be migrated against in a follow-up.
 *  - It is NOT a configurable workflow builder. There is exactly one
 *    workflow for every mission; channels and policies branch INSIDE
 *    states, not by replacing the state graph.
 */

/** Canonical mission lifecycle states (governance roadmap §1, line 50). */
export type WorkflowState =
  | "intake"
  | "gathering_context"
  | "planning"
  | "awaiting_plan_approval"
  | "plan_changes_requested"
  | "plan_approved"
  | "executing"
  | "awaiting_execution_approval"
  | "validating"
  | "validation_failed"
  | "communicating"
  | "completed"
  | "escalated"
  | "halted"
  | "aborted";

/** Why a transition fired — kept narrow so audit reads are searchable. */
export type TransitionReason =
  | "intake_received"
  | "context_gathered"
  | "plan_created"
  | "plan_approval_requested"
  | "plan_changes_requested"
  | "plan_revised"
  | "plan_approved"
  | "plan_rejected"
  | "execution_started"
  | "execution_approval_requested"
  | "execution_approved"
  | "execution_rejected"
  | "validation_started"
  | "validation_passed"
  | "validation_failed"
  | "communication_started"
  | "communication_completed"
  | "mission_completed"
  | "mission_failed"
  | "mission_aborted"
  | "operator_halted"
  | "operator_resumed"
  | "operator_escalated";

export type Transition = {
  from: WorkflowState;
  to: WorkflowState;
  reason: TransitionReason;
};

/** Terminal states have no outgoing transitions. */
export const TERMINAL_STATES: ReadonlySet<WorkflowState> = new Set([
  "completed", "aborted",
]);

export function isTerminal(state: WorkflowState): boolean {
  return TERMINAL_STATES.has(state);
}

/**
 * The declarative allowed-transitions table. Adding a new edge is a
 * deliberate, reviewed decision — if a route wants to fire a transition
 * the table doesn't list, the route is wrong (or the table needs a row
 * with a recorded reason).
 *
 * Key invariants encoded here:
 *  - `planning → executing` is NOT a direct edge. The only ways to reach
 *    `executing` are through `plan_approved`, an approved
 *    `awaiting_execution_approval`, an `escalated` operator override, or
 *    a `halted` operator resume. The plan-gate is therefore not bypassable
 *    via the workflow language itself.
 *  - `awaiting_execution_approval` is the umbrella for the commit / PR /
 *    deploy / customer-comms gates (fase-5 sub-states). It is entered from
 *    `executing` when a significant action is proposed and exited only on
 *    a recorded decision.
 *  - `escalated` and `halted` are not terminal — a human operator can
 *    resume back to planning or executing, or abort. They exist to make
 *    "stuck on a person" explicit instead of letting a mission silently
 *    sit in `awaiting_*` past policy timeouts.
 */
export const ALLOWED_TRANSITIONS: readonly Transition[] = Object.freeze([
  /* ── intake → ── */
  { from: "intake",            to: "gathering_context",          reason: "intake_received" },
  // Deployment lifecycle root (mig 192): a gate-only run born directly at
  // its execution gate — it never plans or executes.
  { from: "intake",            to: "awaiting_execution_approval",reason: "execution_approval_requested" },
  { from: "intake",            to: "aborted",                    reason: "mission_aborted" },

  /* ── gathering_context → ── */
  { from: "gathering_context", to: "planning",                   reason: "context_gathered" },
  { from: "gathering_context", to: "halted",                     reason: "operator_halted" },
  { from: "gathering_context", to: "aborted",                    reason: "mission_aborted" },

  /* ── planning → ── */
  { from: "planning",          to: "awaiting_plan_approval",     reason: "plan_approval_requested" },
  // Policy may skip the plan gate (low-risk / explicit no-approval):
  { from: "planning",          to: "plan_approved",              reason: "plan_created" },
  { from: "planning",          to: "halted",                     reason: "operator_halted" },
  { from: "planning",          to: "aborted",                    reason: "mission_aborted" },

  /* ── awaiting_plan_approval → ── */
  { from: "awaiting_plan_approval", to: "plan_approved",         reason: "plan_approved" },
  { from: "awaiting_plan_approval", to: "plan_changes_requested",reason: "plan_changes_requested" },
  { from: "awaiting_plan_approval", to: "aborted",               reason: "plan_rejected" },
  { from: "awaiting_plan_approval", to: "escalated",             reason: "operator_escalated" },
  { from: "awaiting_plan_approval", to: "halted",                reason: "operator_halted" },

  /* ── plan_changes_requested → ── */
  { from: "plan_changes_requested", to: "planning",              reason: "plan_revised" },
  { from: "plan_changes_requested", to: "aborted",               reason: "mission_aborted" },
  { from: "plan_changes_requested", to: "halted",                reason: "operator_halted" },

  /* ── plan_approved → ── */
  { from: "plan_approved",     to: "executing",                  reason: "execution_started" },
  { from: "plan_approved",     to: "halted",                     reason: "operator_halted" },
  { from: "plan_approved",     to: "aborted",                    reason: "mission_aborted" },

  /* ── executing → ── */
  { from: "executing",         to: "awaiting_execution_approval",reason: "execution_approval_requested" },
  { from: "executing",         to: "validating",                 reason: "validation_started" },
  { from: "executing",         to: "completed",                  reason: "mission_completed" },
  { from: "executing",         to: "escalated",                  reason: "operator_escalated" },
  { from: "executing",         to: "halted",                     reason: "operator_halted" },
  { from: "executing",         to: "aborted",                    reason: "mission_failed" },

  /* ── awaiting_execution_approval → ── (commit / PR / deploy / comms) */
  { from: "awaiting_execution_approval", to: "executing",        reason: "execution_approved" },
  { from: "awaiting_execution_approval", to: "validating",       reason: "execution_approved" },
  // Deployment lifecycle root (mig 192): an approved deployment gate IS the
  // run's entire purpose — settling it completes the run directly.
  { from: "awaiting_execution_approval", to: "completed",        reason: "mission_completed" },
  { from: "awaiting_execution_approval", to: "aborted",          reason: "execution_rejected" },
  { from: "awaiting_execution_approval", to: "escalated",        reason: "operator_escalated" },
  { from: "awaiting_execution_approval", to: "halted",           reason: "operator_halted" },

  /* ── validating → ── */
  { from: "validating",        to: "validation_failed",          reason: "validation_failed" },
  { from: "validating",        to: "communicating",              reason: "communication_started" },
  { from: "validating",        to: "completed",                  reason: "validation_passed" },
  { from: "validating",        to: "halted",                     reason: "operator_halted" },

  /* ── validation_failed → ── */
  // Re-plan with the failure as input (mirrors the auto-revise lane).
  { from: "validation_failed", to: "planning",                   reason: "plan_revised" },
  { from: "validation_failed", to: "aborted",                    reason: "mission_aborted" },
  { from: "validation_failed", to: "halted",                     reason: "operator_halted" },

  /* ── communicating → ── */
  { from: "communicating",     to: "completed",                  reason: "communication_completed" },
  { from: "communicating",     to: "halted",                     reason: "operator_halted" },

  /* ── escalated → ── (a human takes the call) */
  { from: "escalated",         to: "planning",                   reason: "plan_revised" },
  { from: "escalated",         to: "executing",                  reason: "execution_started" },
  { from: "escalated",         to: "aborted",                    reason: "mission_aborted" },
  { from: "escalated",         to: "halted",                     reason: "operator_halted" },

  /* ── halted → ── (operator unpauses) */
  // Resume routes back to where we were; modelled as a small fan-out
  // because the static table doesn't remember the previous state.
  { from: "halted",            to: "planning",                   reason: "operator_resumed" },
  { from: "halted",            to: "executing",                  reason: "operator_resumed" },
  { from: "halted",            to: "communicating",              reason: "operator_resumed" },
  { from: "halted",            to: "aborted",                    reason: "mission_aborted" },
]);

const ADJACENCY: ReadonlyMap<WorkflowState, ReadonlyArray<Transition>> = (() => {
  const m = new Map<WorkflowState, Transition[]>();
  for (const t of ALLOWED_TRANSITIONS) {
    const arr = m.get(t.from) ?? [];
    arr.push(t);
    m.set(t.from, arr);
  }
  return m;
})();

/** Pure boolean — is `(from, to)` listed as an allowed edge (regardless of reason). */
export function canTransition(from: WorkflowState, to: WorkflowState): boolean {
  if (isTerminal(from)) return false;
  return ALLOWED_TRANSITIONS.some((t) => t.from === from && t.to === to);
}

/** All outgoing edges from a state — for "what can happen next" hints in audit. */
export function allowedTransitionsFrom(from: WorkflowState): ReadonlyArray<Transition> {
  return ADJACENCY.get(from) ?? [];
}

/** Throw on invalid transition. Returns the resolved Transition row when
 *  the edge IS allowed — including its canonical reason if the caller
 *  passed one that matches. */
export function assertTransition(
  from: WorkflowState,
  to: WorkflowState,
  reason: TransitionReason,
): Transition {
  const matches = ALLOWED_TRANSITIONS.filter((t) => t.from === from && t.to === to);
  if (matches.length === 0) {
    throw new Error(`workflow: blocked transition ${from} → ${to} (no allowed edge)`);
  }
  const exact = matches.find((t) => t.reason === reason);
  if (!exact) {
    const allowedReasons = matches.map((t) => t.reason).join(" | ");
    throw new Error(
      `workflow: transition ${from} → ${to} requires one of [${allowedReasons}]; got ${reason}`,
    );
  }
  return exact;
}

/**
 * Project the current persisted `RunStatus` onto the canonical workflow
 * state. Lossy by design: a `RunStatus` of "awaiting_approval" doesn't
 * say whether the open gate is plan vs execution; the caller can pass
 * `hint` to disambiguate. When the engine is rewritten to persist a
 * canonical state directly, this projection goes away.
 *
 * Mapping (codify-what-works):
 *   queued              → intake                  (mission seeded, not started)
 *   planning            → planning
 *   delegating          → executing               (delegated to child agent)
 *   running             → executing
 *   awaiting_approval   → awaiting_plan_approval   (default)
 *                       → awaiting_execution_approval (hint: any non-plan)
 *   blocked             → halted
 *   completed           → completed
 *   failed              → aborted                 (mission ended unsuccessfully)
 *   cancelled           → aborted
 */
export type RunStatusLike =
  | "queued" | "planning" | "delegating" | "running"
  | "awaiting_approval" | "blocked" | "completed" | "failed" | "cancelled";

export type ApprovalTypeHint =
  | "plan" | "risky_patch" | "deployment" | "retry"
  | "security_override" | "ui_change_approval" | "ux_regression_override"
  // `development_review` (mig 225) is an execution-side gate: it projects
  // onto `awaiting_execution_approval` like every other non-plan type.
  // `infra_action` (mig 238) is the same shape: a human gate in front of a
  // concrete action, so it projects onto `awaiting_execution_approval` too.
  | "commit" | "pr_merge" | "development_review" | "infra_action";

export function mapRunStatusToWorkflowState(
  status: RunStatusLike,
  hint?: { openApprovalType?: ApprovalTypeHint | null } | null,
): WorkflowState {
  switch (status) {
    case "queued":     return "intake";
    case "planning":   return "planning";
    case "delegating": return "executing";
    case "running":    return "executing";
    case "awaiting_approval": {
      const t = hint?.openApprovalType ?? null;
      // `plan` (and only `plan`) is the pre-execution governance gate.
      // Everything else is an execution-side gate (commit/PR/deploy/comms).
      return t === "plan" || t === null ? "awaiting_plan_approval" : "awaiting_execution_approval";
    }
    case "blocked":    return "halted";
    case "completed":  return "completed";
    case "failed":     return "aborted";
    case "cancelled":  return "aborted";
  }
}

/** Programmatic enumeration so the smoke + UI can iterate without
 *  hard-coding the union list (the union is the source of truth; this
 *  array is a derived value pinned by a TypeScript check). */
export const WORKFLOW_STATES: readonly WorkflowState[] = Object.freeze([
  "intake", "gathering_context", "planning",
  "awaiting_plan_approval", "plan_changes_requested", "plan_approved",
  "executing", "awaiting_execution_approval",
  "validating", "validation_failed", "communicating",
  "completed", "escalated", "halted", "aborted",
]);

// Compile-time exhaustiveness check: if a new state is added to the
// WorkflowState union without listing it in WORKFLOW_STATES (or vice
// versa), one of the two `extends never` assignments below fails to
// type-check. The runtime test additionally pins the length.
type _StatesArrayUnion = (typeof WORKFLOW_STATES)[number];
type _MissingFromArray = Exclude<WorkflowState, _StatesArrayUnion>;
type _ExtraInArray = Exclude<_StatesArrayUnion, WorkflowState>;
const _missingFromArray: _MissingFromArray extends never ? true : false = true;
const _extraInArray: _ExtraInArray extends never ? true : false = true;
void _missingFromArray; void _extraInArray;
